<template>
    <div class="login-container">
        <!-- Background Elements -->
        <div class="background-elements">
            <div class="bg-circle circle-1"></div>
            <div class="bg-circle circle-2"></div>
            <div class="bg-circle circle-3"></div>
        </div>

        <!-- Main Content -->
        <div class="container-fluid h-100">
            <div class="row h-100 align-items-center justify-content-center">
                <!-- Centered Reset Password Form -->
                <div class="col-lg-5 col-md-7 col-11">
                    <div class="login-section">
                        <div class="login-card">
                            <div class="login-header">
                                <div class="brand-logo-center mb-4">
                                    <div class="logo-icon">
                                        <i class="bi bi-shield-fill-check"></i>
                                    </div>
                                </div>

                                <h3 class="login-title">Reset Password</h3>
                                <p class="login-subtitle">Enter your new password below</p>
                            </div>

                            <form class="login-form" @submit.prevent="handleSubmit">
                                <!-- Email Field (readonly) -->
                                <div class="form-group mb-3">
                                    <label for="email" class="form-label">
                                        <i class="bi bi-envelope me-2"></i>Email Address
                                    </label>
                                    <input type="email" class="form-control" :class="{ 'is-invalid': form.errors.email }" id="email" v-model="form.email" readonly>
                                    <div v-if="form.errors.email" class="invalid-feedback">
                                        {{ form.errors.email }}
                                    </div>
                                </div>

                                <!-- Password Field -->
                                <div class="form-group mb-3">
                                    <label for="password" class="form-label">
                                        <i class="bi bi-lock me-2"></i>New Password
                                    </label>
                                    <div class="input-group">
                                        <input :type="showPassword ? 'text' : 'password'" class="form-control" :class="{ 'is-invalid': form.errors.password }" id="password" v-model="form.password" placeholder="Enter new password" required>
                                        <button type="button" class="input-group-text password-toggle" @click="togglePassword">
                                            <i :class="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                                        </button>
                                    </div>
                                    <div v-if="form.errors.password" class="invalid-feedback d-block">
                                        {{ form.errors.password }}
                                    </div>
                                    <small class="text-muted">Must be at least 8 characters</small>
                                </div>

                                <!-- Confirm Password Field -->
                                <div class="form-group mb-4">
                                    <label for="password_confirmation" class="form-label">
                                        <i class="bi bi-lock-fill me-2"></i>Confirm New Password
                                    </label>
                                    <div class="input-group">
                                        <input :type="showPasswordConfirmation ? 'text' : 'password'" class="form-control" :class="{ 'is-invalid': form.errors.password_confirmation }" id="password_confirmation" v-model="form.password_confirmation" placeholder="Confirm new password" required>
                                        <button type="button" class="input-group-text password-toggle" @click="togglePasswordConfirmation">
                                            <i :class="showPasswordConfirmation ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                                        </button>
                                    </div>
                                    <div v-if="form.errors.password_confirmation" class="invalid-feedback d-block">
                                        {{ form.errors.password_confirmation }}
                                    </div>
                                </div>

                                <!-- Password Strength Indicator -->
                                <div v-if="form.password" class="password-strength mb-4">
                                    <div class="strength-bar-container">
                                        <div class="strength-bar" :style="{ width: passwordStrength.percentage + '%', backgroundColor: passwordStrength.color }"></div>
                                    </div>
                                    <small :style="{ color: passwordStrength.color }">
                                        Password Strength: {{ passwordStrength.text }}
                                    </small>
                                </div>

                                <!-- Submit Button -->
                                <button type="submit" class="btn btn-login" :disabled="form.processing">
                                    <span v-if="form.processing">
                                        <i class="bi bi-arrow-repeat spinning me-2"></i>
                                        Resetting...
                                    </span>
                                    <span v-else>
                                        <i class="bi bi-check-circle me-2"></i>
                                        Reset Password
                                    </span>
                                </button>

                                <!-- Back to Login Link -->
                                <div class="signup-link">
                                    <p><a href="/">Back to Login</a></p>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useForm } from '@inertiajs/vue3';
import { useNotifications } from '@/composables/useNotifications';

// Props
const props = defineProps({
    token: String,
    email: String,
});

// Initialize notifications
const { success, error } = useNotifications();

// Form data using Inertia's useForm for better error handling
const form = useForm({
    token: props.token,
    email: props.email,
    password: '',
    password_confirmation: '',
});

// UI state
const showPassword = ref(false);
const showPasswordConfirmation = ref(false);

// Toggle password visibility
const togglePassword = () => {
    showPassword.value = !showPassword.value;
};

const togglePasswordConfirmation = () => {
    showPasswordConfirmation.value = !showPasswordConfirmation.value;
};

// Password strength calculation
const passwordStrength = computed(() => {
    const password = form.password;
    if (!password) return { percentage: 0, text: '', color: '' };

    let strength = 0;

    // Length check
    if (password.length >= 8) strength += 25;
    if (password.length >= 12) strength += 10;

    // Character variety checks
    if (/[a-z]/.test(password)) strength += 15;
    if (/[A-Z]/.test(password)) strength += 15;
    if (/[0-9]/.test(password)) strength += 15;
    if (/[^a-zA-Z0-9]/.test(password)) strength += 20;

    let text = '';
    let color = '';

    if (strength < 40) {
        text = 'Weak';
        color = '#dc3545';
    } else if (strength < 70) {
        text = 'Medium';
        color = '#ffc107';
    } else {
        text = 'Strong';
        color = '#28a745';
    }

    return { percentage: strength, text, color };
});

// Handle form submission
const handleSubmit = () => {
    form.post('/reset-password', {
        preserveScroll: true,
        onSuccess: () => {
            success('Password reset successfully! You can now login with your new password.');
        },
        onError: () => {
            error('Failed to reset password. Please try again.');
        },
    });
};
</script>

<style scoped>
/* Login Container */
.login-container {
    min-height: 100vh;
    background: linear-gradient(135deg, #1e9579 0%, #2dd4bf 100%);
    position: relative;
    overflow: hidden;
    font-family: 'Fira Sans', sans-serif;
    display: flex;
    align-items: center;
    justify-content: center;
}

/* Background Elements */
.background-elements {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 1;
}

.bg-circle {
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
}

.circle-1 {
    width: 300px;
    height: 300px;
    top: -100px;
    left: -100px;
}

.circle-2 {
    width: 400px;
    height: 400px;
    bottom: -150px;
    right: -150px;
}

.circle-3 {
    width: 200px;
    height: 200px;
    top: 50%;
    left: 10%;
    transform: translateY(-50%);
}

/* Login Section */
.login-section {
    position: relative;
    z-index: 2;
}

.login-card {
    background: white;
    border-radius: 24px;
    padding: 3rem;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
}

.login-header {
    text-align: center;
    margin-bottom: 2rem;
}

.brand-logo-center {
    display: flex;
    justify-content: center;
    align-items: center;
}

.logo-icon {
    width: 80px;
    height: 80px;
    background: linear-gradient(135deg, #1e9579 0%, #2dd4bf 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 8px 20px rgba(30, 149, 121, 0.3);
}

.logo-icon i {
    font-size: 2.5rem;
    color: white;
}

.login-title {
    font-size: 2rem;
    font-weight: 700;
    color: #1e9579;
    margin-bottom: 0.5rem;
}

.login-subtitle {
    font-size: 1rem;
    color: #6c757d;
    margin-bottom: 0;
}

/* Form Styles */
.login-form .form-label {
    font-weight: 600;
    color: #333;
    margin-bottom: 0.5rem;
    font-size: 0.95rem;
}

.form-control {
    padding: 0.875rem 1rem;
    border: 2px solid #e0e0e0;
    border-radius: 12px;
    font-size: 1rem;
    transition: all 0.3s ease;
}

.form-control:focus {
    border-color: #1e9579;
    box-shadow: 0 0 0 0.2rem rgba(30, 149, 121, 0.15);
}

.form-control.is-invalid {
    border-color: #dc3545;
}

.invalid-feedback {
    font-size: 0.875rem;
    margin-top: 0.25rem;
}

.input-group-text {
    border: 2px solid #e0e0e0;
    border-left: none;
    background-color: #f8f9fa;
    border-radius: 0 12px 12px 0;
}

.password-toggle {
    cursor: pointer;
    transition: all 0.3s ease;
}

.password-toggle:hover {
    background-color: #e9ecef;
}

/* Password Strength Indicator */
.password-strength {
    margin-top: 1rem;
}

.strength-bar-container {
    width: 100%;
    height: 6px;
    background-color: #e0e0e0;
    border-radius: 3px;
    overflow: hidden;
    margin-bottom: 0.5rem;
}

.strength-bar {
    height: 100%;
    transition: all 0.3s ease;
    border-radius: 3px;
}

.btn-login {
    width: 100%;
    padding: 0.875rem;
    background: linear-gradient(135deg, #1e9579 0%, #2dd4bf 100%);
    border: none;
    border-radius: 12px;
    color: white;
    font-size: 1rem;
    font-weight: 600;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(30, 149, 121, 0.3);
}

.btn-login:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(30, 149, 121, 0.4);
}

.btn-login:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.spinning {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}

/* Sign Up Link */
.signup-link {
    text-align: center;
    margin-top: 1.5rem;
}

.signup-link p {
    color: #6c757d;
    font-size: 0.95rem;
    margin: 0;
}

.signup-link a {
    color: #1e9579;
    font-weight: 600;
    text-decoration: none;
    transition: color 0.3s ease;
}

.signup-link a:hover {
    color: #2dd4bf;
    text-decoration: underline;
}

/* Responsive Design */
@media (max-width: 768px) {
    .login-card {
        padding: 2rem 1.5rem;
    }

    .login-title {
        font-size: 1.5rem;
    }

    .logo-icon {
        width: 60px;
        height: 60px;
    }

    .logo-icon i {
        font-size: 2rem;
    }
}
</style>
